"use client"

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react"

export type ActionType = "navigation" | "task"

export interface CommandAction {
  id: string
  label: string
  type: ActionType
  keywords: string[]
  action?: () => void
  onEnter?: () => void
  footerText?: string
  icon?: React.ReactNode
  description?: string
  priority?: number
  group?: string
  shortcut?: {
    keys: string
    formatted?: string | string[]
  }
}

interface CommandPaletteContextValue {
  globalActions: CommandAction[]
  pageActions: CommandAction[]
  registerPageActions: (actions: CommandAction[]) => () => void
  setGlobalActions: (actions: CommandAction[]) => void
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | undefined>(undefined)

interface CommandPaletteProviderProps {
  children: React.ReactNode
  initialGlobalActions?: CommandAction[]
}

export function CommandPaletteProvider({ children, initialGlobalActions = [] }: CommandPaletteProviderProps) {
  const [globalActions, setGlobalActions] = useState<CommandAction[]>(initialGlobalActions)
  const [pageActionSets, setPageActionSets] = useState<Map<string, CommandAction[]>>(new Map())
  const actionSetIdRef = useRef(0)

  const pageActions = useMemo(() => {
    const allPageActions: CommandAction[] = []
    pageActionSets.forEach((actions) => {
      allPageActions.push(...actions)
    })
    return allPageActions.sort((a, b) => (b.priority || 0) - (a.priority || 0))
  }, [pageActionSets])

  // Filter global actions to exclude any that have the same ID as page actions
  const filteredGlobalActions = useMemo(() => {
    const pageActionIds = new Set(pageActions.map((action) => action.id))
    return globalActions.filter((action) => !pageActionIds.has(action.id))
  }, [globalActions, pageActions])

  const registerPageActions = useCallback((actions: CommandAction[]) => {
    const id = String(++actionSetIdRef.current)
    setPageActionSets((prev) => new Map(prev).set(id, actions))

    return () => {
      setPageActionSets((prev) => {
        const next = new Map(prev)
        next.delete(id)
        return next
      })
    }
  }, [])

  const value = useMemo(
    () => ({
      globalActions: filteredGlobalActions,
      pageActions,
      registerPageActions,
      setGlobalActions,
    }),
    [filteredGlobalActions, pageActions, registerPageActions]
  )

  return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>
}

export function useCommandPaletteContext() {
  const context = useContext(CommandPaletteContext)
  if (!context) {
    throw new Error("useCommandPaletteContext must be used within a CommandPaletteProvider")
  }
  return context
}
